/**
 * Navigation du back-office — groupée par **moment du métier**, pas par table :
 * on configure une fois, on monte une saison chaque printemps, on mène une
 * campagne, on tient le guichet toute l’année, on suit les chiffres en continu
 * (SPEC-FONCTIONNELLE §16).
 *
 * Chaque entrée porte les rôles qui la voient. C’est du confort d’affichage :
 * l’autorisation réelle est vérifiée par le backend.
 */

import {
  peutOuvrirEcran,
  type DroitMetier,
  type RoleUtilisateur,
  type TypeTenantPublic,
} from '@tb/shared'

/* Le chemin de l’écran d’apparence est exporté par sa feature et non par
   `lib/chemins.ts` : voir le commentaire de `features/apparence/index.tsx`. */
import { CHEMIN_APPARENCE } from '../features/apparence'
/* Même raison pour la régie (SPEC-BOUTIQUE §7). Les cinq outils sont nommés un
   par un : chacun a sa page d'aide, et sans eux les cinq héritaient de celle du
   diagnostic — quatre aides fausses sur cinq. */
import {
  CHEMIN_ABONNEMENTS,
  CHEMIN_DIAGNOSTIC,
  CHEMIN_ESPACE_ABONNE,
  CHEMIN_PANIERS_REGIE,
  CHEMIN_PHASES,
  CHEMIN_REGIE,
} from '../features/deblocage'
import { CHEMINS } from '../lib/chemins'
import {
  capitaliser,
  descriptionApparence,
  type Lexique,
  lexique,
  titreConfiguration,
} from '../lib/vocabulaire'

export interface EntreeNavigation {
  readonly libelle: string
  readonly to: string
  /** Rôles autorisés — liste explicite, jamais de hiérarchie implicite. */
  readonly roles: readonly RoleUtilisateur[]
  /**
   * ★ EVT-04 — `true` quand l'écran n'a **rien à décrire** dans un tenant de
   * type `evenement` : campagne de réabonnement, formules, reconduction.
   *
   * Ce n'est pas une restriction de droits. Un organisateur de fête
   * d'entreprise est administrateur chez lui, et l'API ne lui refuse rien : ces
   * écrans s'ouvriraient, vides. Les retirer du menu, c'est lui éviter de
   * chercher la vente au milieu de fonctions qui parlent d'une saison qu'il
   * n'a pas, d'abonnés qu'il n'aura jamais et d'une campagne qui n'existera
   * pas. Le libre-service intégral (SPEC-BOUTIQUE §8 ter D : « aucun
   * accompagnement inclus ») se joue exactement là.
   */
  readonly masqueSiEvenement?: boolean
  /** Le lien quitte le back-office (app de scan). */
  readonly externe?: boolean
  readonly description?: string
  /**
   * ★ La RÉFÉRENCE de documentation qui décrit CET écran — par exemple
   * `aide/paiements/impayes`, ou `aide/tarification/grille#la-matrice`.
   *
   * Elle vit ici et nulle part ailleurs pour la même raison que le reste de ce
   * fichier : la coque connaît déjà chaque écran, son libellé, ses rôles et son
   * chemin. Poser l'aide écran par écran obligerait à toucher quinze fichiers,
   * et le seizième serait oublié — c'est exactement le motif que six vagues de
   * garde-fous ont passé leur temps à rattraper.
   *
   * ★★ **Le FRAGMENT est la nouveauté, et il n'est pas décoratif.** Les pages
   * d'aide font maintenant six écrans de long : viser le haut de
   * `aide/tarification/grille` depuis l'écran de tarification, c'est déposer
   * l'utilisateur devant une table des matières. On vise donc la section qui
   * parle de l'écran — mais SEULEMENT quand il y en a une qui le fait mieux que
   * l'introduction. Sur `Apparence` ou `Imports`, dont la page entière décrit
   * l'écran et rien d'autre, sauter l'introduction serait une perte sèche : ces
   * entrées gardent leur ancre nue, et c'est un choix, pas un oubli.
   *
   * Facultative dans le TYPE, exigée par le test : `navigation-aide.test.ts`
   * refuse une entrée sans référence, refuse une référence dont la page
   * n'existe pas, et refuse un fragment qui ne désigne aucune section. Le type
   * reste permissif pour que l'oubli échoue avec un message lisible plutôt
   * qu'avec une erreur de compilation obscure.
   */
  readonly aide?: string
  /**
   * ★ Les écrans PROFONDS de cette entrée qui ont leur propre documentation.
   *
   * Une entrée de menu couvre souvent plusieurs écrans : `Réabonnement` mène à
   * cinq étapes, `Régie` à cinq outils, `Paiements` à la liste des impayés, aux
   * fiches, au compte bancaire et au modèle de relance. La correspondance par
   * préfixe donnait à tous la même aide — celle de l'entrée — c'est-à-dire une
   * aide FAUSSE sur quatre écrans de régie sur cinq, alors que la page qui les
   * décrit existait, écrite, publiée, et injoignable.
   *
   * ⚠️ C'est une liste écrite à la main, donc exactement le genre d'objet que
   * ce projet sait mal tenir. Deux choses la rendent tenable : elle ne contient
   * QUE des écrans qui ont une page à eux (pas un par route), et le garde-fou
   * vérifie chaque référence. Un sous-écran oublié retombe sur l'aide de son
   * entrée : dégradé, jamais faux.
   */
  readonly sousEcrans?: readonly SousEcranNavigation[]
}

/** Un écran profond, sous une entrée de menu, qui a sa propre documentation. */
export interface SousEcranNavigation {
  /** Le préfixe d'adresse, par exemple `/admin/regie/paniers`. */
  readonly to: string
  /** Sa référence de documentation, fragment compris. */
  readonly aide: string
}

export interface GroupeNavigation {
  readonly titre: string
  readonly entrees: readonly EntreeNavigation[]
}

const TOUS_STAFF: readonly RoleUtilisateur[] = ['admin', 'guichet', 'lecture', 'controle']
const ADMIN: readonly RoleUtilisateur[] = ['admin']
const GUICHET: readonly RoleUtilisateur[] = ['admin', 'guichet']
const SCAN: readonly RoleUtilisateur[] = ['admin', 'guichet', 'controle']
/** Les chiffres de la maison : direction et administration, pas le guichet. */
const DIRECTION: readonly RoleUtilisateur[] = ['admin', 'lecture']

/**
 * ★★ LE MENU EST UNE FONCTION DU LEXIQUE, ET C'EST TOUT LE POINT.
 *
 * La barre latérale s'affiche sur les ONZE écrans du back-office. Le mot
 * « Théâtre » et la rubrique « SAISON » y étaient donc lus onze fois par un
 * organisateur de fête d'entreprise — et c'est **un seul endroit** à corriger,
 * pas onze. Les intitulés qui dépendent du tenant viennent de
 * `lib/vocabulaire.ts` ; ceux qui n'en dépendent pas restent écrits ici.
 *
 * ⚠️ `description` alimente l'attribut `title` du lien (`LayoutAdmin`) : c'est
 * ce qu'annonce un lecteur d'écran, et c'est la surface où le défaut s'était
 * logé sans que personne ne le voie — « Reprise des abonnés et de l'historique »
 * posée sur un menu d'événement. Elle se traite comme un texte visible.
 */
function groupesDeNavigation(mots: Lexique, type: TypeTenantPublic): GroupeNavigation[] {
  return [
    {titre:'Canal partenaire',entrees:[{libelle:'Mon espace partenaire',to:'/admin/partenaire',roles:['partenaire'],aide:'aide/guichet/partenaires'}]},
    {
      titre: 'Configuration',
      entrees: [
        {
          libelle: titreConfiguration(type),
          to: CHEMINS.configuration,
          aide: 'aide/configuration/identite',
          roles: ADMIN,
          /* ★★ LIB-03 — LA COQUE NE SAIT PAS DANS QUEL LIEU ELLE S'AFFICHE.
             Elle est rendue pour les trois rôles, avant toute requête de
             configuration, et un même théâtre peut jouer en salle numérotée et
             sous chapiteau la même saison (0012) : aucune donnée ne lui
             permettrait de choisir entre « plan de salle » et « jauge ». Une
             infobulle promettait donc un plan à quinze écrans d'un lieu qui
             n'en a pas. Elle nomme désormais les ONGLETS de l'écran, ce qui est
             vrai des deux côtés et se vérifie en l'ouvrant. */
          description: 'Identité, lieu, catégories de public, déductions, comptes',
        },
        {
          libelle: 'Apparence',
          to: CHEMIN_APPARENCE,
          aide: 'aide/apparence/couleurs',
          roles: ADMIN,
          description: descriptionApparence(type),
        },
        /* EVT-04 — la reprise d'un fichier suppose une saison passée, des abonnés
           et des formules à faire correspondre. Un événement ponctuel n'a pas de
           « saison passée » : l'écran entier est sans objet, et son adresse renvoie
           au back-office (`features/imports/index.tsx`). */
        {
          libelle: 'Imports',
          to: CHEMINS.imports,
          aide: 'aide/imports/abonnes',
          roles: ADMIN,
          description: 'Reprise des abonnés et de l’historique',
          masqueSiEvenement: true,
        },
      ],
    },
    {
      titre: capitaliser(mots.saison),
      entrees: [
        {
          libelle: 'Occupation des lieux',
          to: '/admin/planning',
          roles: ['admin', 'guichet', 'lecture'],
          aide: 'aide/regie/planning',
        },
        {
          libelle: 'Catalogue',
          to: CHEMINS.catalogue,
          roles: ADMIN,
          aide: 'aide/catalogue/saison',
        },
        {
          libelle: 'Tarification',
          to: CHEMINS.tarification,
          roles: ADMIN,
          /* La page s'ouvre sur les trois portées d'un tarif — utile une fois,
             au premier montage. Ce que l'écran pose tous les jours, c'est
             « comment je remplis cette matrice ». */
          aide: 'aide/tarification/grille#la-matrice',
        },
        /* EVT-04 — une formule d'abonnement suppose une saison de plusieurs
           spectacles. Un événement ponctuel en a un seul, et LIB-07 interdit de
           toute façon la série sur un lieu à placement libre. */
        {
          libelle: 'Formules',
          to: CHEMINS.formules,
          roles: ADMIN,
          masqueSiEvenement: true,
          aide: 'aide/formules/creer',
        },
      ],
    },
    {
      titre: 'Campagne',
      entrees: [
        {
          libelle: 'Réabonnement',
          to: CHEMINS.campagne,
          /* L'entrée mène au TABLEAU DE BORD de la campagne, pas au calendrier
             des phases : c'est la première section de la page « à replacer »
             qui le décrit, colonne par colonne. */
          aide: 'aide/campagne/a-replacer#le-tableau-de-bord',
          roles: GUICHET,
          masqueSiEvenement: true,
          /* Les quatre étapes de la campagne ont chacune leur page — écrite,
             publiée, et jusqu'ici injoignable depuis l'écran qu'elle décrit. */
          sousEcrans: [
            { to: `${CHEMINS.campagne}/correspondances`, aide: 'aide/campagne/correspondances' },
            { to: `${CHEMINS.campagne}/generation`, aide: 'aide/campagne/propositions' },
            { to: `${CHEMINS.campagne}/bulletins`, aide: 'aide/campagne/bulletins' },
            { to: `${CHEMINS.campagne}/phases`, aide: 'aide/campagne/phases' },
          ],
        },
      ],
    },
    {
      titre: 'Guichet',
      entrees: [
        /* EVT-04 — une souscription est un abonnement : sans formule, l'écran
           n'a rien à proposer. */
        {
          libelle: 'Souscriptions',
          to: CHEMINS.souscriptions,
          roles: GUICHET,
          masqueSiEvenement: true,
          /* L'entrée ouvre la LISTE des abonnements ; la création est un
             sous-écran, et c'est lui que le reste de la page décrit. */
          aide: 'aide/guichet/souscription#liste-des-souscriptions',
          sousEcrans: [
            { to: `${CHEMINS.souscriptions}/nouvelle`, aide: 'aide/guichet/souscription' },
          ],
        },
        {
          libelle: 'Ventes et invitations',
          to: CHEMINS.ventes,
          roles: GUICHET,
          aide: 'aide/guichet/vendre',
          sousEcrans: [
            { to: `${CHEMINS.ventes}/journal`, aide: 'aide/guichet/vendre#ventes-du-jour' },
          ],
        },
        {
          libelle: 'Vente téléphonique',
          to: CHEMINS.paniers,
          aide: 'aide/guichet/telephone',
          roles: GUICHET,
          description: 'Le même parcours que la boutique, pour un acheteur au téléphone',
          sousEcrans: [
            { to: CHEMINS.paniersOuverts, aide: 'aide/guichet/telephone#paniers-ouverts' },
          ],
        },
        {
          libelle: 'Délégations financières',
          to: '/admin/finance',
          roles: ['admin', 'guichet'],
          aide: 'aide/paiements/delegations',
          description:
            'Périmètres, plafonds, seconde approbation et journal des opérations déléguées.',
        },
        {
          libelle: 'Caisses',
          to: CHEMINS.caisse,
          aide: 'aide/paiements/encaisser#sessions-de-caisse',
          roles: GUICHET,
          description: 'Ouvrir, compter et clôturer chaque poste',
        },
        {
          libelle: 'Annulations à suivre',
          to: CHEMINS.incidents,
          aide: 'aide/catalogue/incident',
          roles: GUICHET,
          description: 'Décisions par commande et suivi des personnes à contacter',
        },
        {
          libelle: 'Demandes spectateurs',
          to: '/admin/ventes/demandes',
          roles: GUICHET,
          aide: 'aide/guichet/demandes',
        },
        {
          libelle: 'Liste d’attente',
          to: '/admin/ventes/attente',
          roles: GUICHET,
          aide: 'aide/guichet/attente',
        },
        {
          libelle: 'Groupes et écoles',
          to: '/admin/ventes/groupes',
          roles: GUICHET,
          aide: 'aide/guichet/groupes',
        },
        {
          libelle:'Distribution des billets',to:'/admin/distribution',roles:GUICHET,aide:'aide/guichet/distribution',
        },
        {
          libelle:'Accès et portes',to:'/admin/acces',roles:ADMIN,aide:'aide/controle/avance',
        },
        {
          libelle: 'Partenaires',to:'/admin/partenaires',roles:['admin'],aide:'aide/guichet/partenaires',
        },
        {
          libelle: 'Promotions',
          to: '/admin/ventes/promotions',
          roles: ['admin'],
          aide: 'aide/guichet/promotions',
        },
        {
          libelle: 'Impression des billets',
          to: '/admin/ventes/impressions',
          roles: GUICHET,
          aide: 'aide/guichet/impressions',
        },
        {
          libelle: 'Bons cadeaux et crédits',
          to: '/admin/ventes/bons',
          roles: GUICHET,
          aide: 'aide/guichet/bons',
        },
        {
          libelle: 'Secours guichet',
          to: '/admin/ventes/secours',
          roles: GUICHET,
          aide: 'aide/guichet/secours',
        },
        {
          libelle: 'Rapprochement bancaire',
          to: CHEMINS.rapprochement,
          aide: 'aide/paiements/qr-facture#au-virement',
          roles: ['admin', 'lecture'],
          description: 'Importer et vérifier les virements reçus',
        },
        {
          libelle: 'Journal comptable',
          to: CHEMINS.comptabilite,
          aide: 'aide/paiements/comptabilite',
          roles: ['admin', 'lecture'],
          description: 'Ventiler les pièces, rapprocher les frais et figer le journal',
        },
        {
          libelle: 'Paiements',
          to: CHEMINS.paiements,
          roles: GUICHET,
          /* ★ L'entrée ouvre la liste des IMPAYÉS, pas une fiche de paiement.
             Elle pointait « Encaisser un paiement » : une page juste, sur un
             autre écran. L'encaissement est l'aide des FICHES, ci-dessous. */
          aide: 'aide/paiements/impayes#lire-la-liste',
          sousEcrans: [
            { to: `${CHEMINS.paiements}/souscription`, aide: 'aide/paiements/encaisser' },
            { to: `${CHEMINS.paiements}/vente`, aide: 'aide/paiements/encaisser' },
            {
              to: `${CHEMINS.paiements}/banque`,
              aide: 'aide/paiements/qr-facture#coordonnees-bancaires',
            },
            {
              to: `${CHEMINS.paiements}/relances`,
              aide: 'aide/paiements/impayes#le-modele-de-relance',
            },
          ],
        },
        {
          libelle: 'Contrôle d’accès',
          to: CHEMINS.scan,
          roles: SCAN,
          externe: true,
          aide: 'aide/controle/scanner',
        },
        /**
         * ★★ LA BOUTIQUE N'AVAIT AUCUNE PORTE DANS LE BACK-OFFICE.
         *
         * La vitrine en fait la QUATRIÈME des six étapes d'une année
         * (« Ouvrir la boutique en ligne »), la documentation lui consacre deux
         * rubriques, et le menu n'en portait aucune trace : sa publication se
         * règle dans un coin du catalogue, et son adresse ne s'apprend nulle
         * part. Un exploitant qui veut simplement VOIR ce que son public voit
         * n'avait pas d'autre moyen que de retaper l'URL de tête.
         *
         * Traitée comme le contrôle d'accès — un lien EXTERNE vers un espace
         * qui a sa propre coque : la boutique est publique, elle ne vit pas
         * sous `/admin` et ne doit pas hériter de son en-tête.
         */
        {
          libelle: 'Boutique en ligne',
          to: CHEMINS.boutique,
          roles: GUICHET,
          externe: true,
          description: 'Voir la billetterie telle que votre public la voit',
          aide: 'aide/boutique/acheter',
        },
      ],
    },
    {
      titre: 'Suivi',
      entrees: [
        {libelle:'Communication et CRM',to:'/admin/crm',roles:ADMIN,aide:'aide/tableaux/communication'},
        {libelle:'Pilotage consolidé',to:'/admin/pilotage',roles:DIRECTION,aide:'aide/tableaux/consolide'},
        {
          libelle: 'Tableaux de bord',
          to: CHEMINS.tableaux,
          roles: DIRECTION,
          aide: 'aide/tableaux/lire',
          /* La fiche d'une soirée n'est ni un onglet ni un export : c'est le
             remplissage d'une date, compteur par compteur. */
          sousEcrans: [
            { to: `${CHEMINS.tableaux}/representations`, aide: 'aide/tableaux/lire#remplissage' },
          ],
        },
      ],
    },
    {
      /* SPEC-BOUTIQUE §7.5 — administration seule : le guichet ne voit même pas
         l’entrée. Placée en dernier, parce qu’on n’y va que quand quelque chose
         coince. */
      titre: 'Régie',
      entrees: [
        {
          libelle: 'Outils de déblocage',
          to: CHEMIN_REGIE,
          aide: 'aide/regie/diagnostic',
          /* ★ Les cinq outils de régie, chacun vers SA page. Sans cette liste,
             « Forcer l'expiration d'un panier » et « Voir l'espace d'un
             abonné » renvoyaient tous deux au diagnostic des places — une aide
             qui ne parle pas de l'écran qu'on a sous les yeux. */
          sousEcrans: [
            { to: CHEMIN_DIAGNOSTIC, aide: 'aide/regie/diagnostic' },
            { to: CHEMIN_ABONNEMENTS, aide: 'aide/regie/abonnements' },
            { to: CHEMIN_PANIERS_REGIE, aide: 'aide/regie/paniers' },
            { to: CHEMIN_PHASES, aide: 'aide/regie/phases' },
            { to: CHEMIN_ESPACE_ABONNE, aide: 'aide/regie/espace-abonne' },
          ],
          roles: ADMIN,
          /* ★ LIB-03 — même raison que l'infobulle de configuration : « place »
             est le mot que le produit emploie déjà quand il ne sait pas encore
             s'il y a un fauteuil derrière (`intitulePlace`, `@tb/shared`). */
          description: 'Comprendre une place prise, déplacer, échanger, libérer, rouvrir une phase',
        },
      ],
    },
  ]
}

/** Le menu tel que le lit un théâtre — la forme complète du produit. */
export const NAVIGATION: readonly GroupeNavigation[] = groupesDeNavigation(
  lexique('client'),
  'client',
)

/**
 * La référence de documentation qui décrit l'écran servi à cette adresse —
 * page, et section quand une section répond mieux que l'introduction.
 *
 * ★ **Correspondance par le PLUS LONG préfixe**, et c'est la seule règle qui
 * tienne : `/admin/catalogue/<id>` doit trouver l'aide du catalogue, et
 * `/admin/regie/paniers` celle des paniers plutôt que celle de la régie en
 * général, alors que `/admin` seul ne doit rien capturer. Un `startsWith` naïf
 * sur des chemins non triés attribuerait l'aide du premier venu.
 *
 * ★ **Les sous-écrans entrent dans la même course**, et gagnent naturellement :
 * leur chemin est plus long que celui de leur entrée. C'est ce qui fait que
 * `/admin/regie/phases` trouve « Rouvrir une phase » et non « Pourquoi cette
 * place est-elle indisponible ». Un sous-écran non déclaré retombe sur l'aide
 * de son entrée : dégradé, jamais faux.
 *
 * ★ **La frontière de segment est vérifiée** : sans elle, `/admin/paniers`
 * capterait `/admin/paniers-archives` s'il en naissait un — le genre de piège
 * qui ne se voit qu'une fois l'écran ajouté, et qui rend alors une aide fausse
 * au lieu de rendre rien. Une aide fausse est pire qu'une aide absente : elle
 * ferme la question.
 *
 * @returns la référence, ou `undefined` quand aucune entrée ne couvre
 *   l'adresse — auquel cas la coque n'affiche simplement pas de lien.
 */
export function aidePourChemin(chemin: string): string | undefined {
  let meilleure: { readonly longueur: number; readonly aide: string } | undefined

  const retenir = (to: string, aide: string): void => {
    if (!chemin.startsWith(to)) return
    const suite = chemin.slice(to.length)
    if (suite !== '' && !suite.startsWith('/')) return
    if (meilleure === undefined || to.length > meilleure.longueur) {
      meilleure = { longueur: to.length, aide }
    }
  }

  for (const groupe of NAVIGATION) {
    for (const entree of groupe.entrees) {
      if (entree.aide !== undefined) retenir(entree.to, entree.aide)
      for (const sousEcran of entree.sousEcrans ?? []) retenir(sousEcran.to, sousEcran.aide)
    }
  }

  return meilleure?.aide
}

/**
 * Les groupes réduits aux entrées visibles pour ce rôle ET pour ce type de
 * tenant ; groupes vides retirés.
 *
 * ★ EVT-04 — `typeTenant` vaut `client` par défaut : **l'interface complète est
 * le défaut**. Un type inconnu, une API qui n'a pas répondu, un mono-tenant —
 * dans tous ces cas on n'ampute rien. Retirer un menu par accident coûterait
 * plus cher que de laisser un organisateur voir un écran qu'il n'ouvrira pas.
 */
export function navigationPour(
  role: RoleUtilisateur | null,
  typeTenant: TypeTenantPublic = 'client',
  droitsMetier?: readonly DroitMetier[] | null,
): readonly GroupeNavigation[] {
  if (role === null) return []
  const evenement = typeTenant === 'evenement'
  return groupesDeNavigation(lexique(typeTenant), typeTenant)
    .map((groupe) => ({
      titre: groupe.titre,
      entrees: groupe.entrees.filter(
        (entree) =>
          entree.roles.includes(role) &&
          peutOuvrirEcran({ droitsMetier }, entree.to) &&
          !(evenement && entree.masqueSiEvenement === true),
      ),
    }))
    .filter((groupe) => groupe.entrees.length > 0)
}

export { TOUS_STAFF }
